import type { FC, ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'clsx';

import styles from './Slider.module.scss';

type SliderOrientation = 'horizontal' | 'vertical';

type SliderProps = {
  className?: string;
  trackClassName?: string;
  fillClassName?: string;
  scrubberClassName?: string;
  tabIndex?: number;
  ariaLabel: string;
  ariaValueText?: string;
  ariaValueMin?: number;
  ariaValueMax?: number;
  ariaValueNow?: number;
  additionalElements?: ReactNode;
  value: number; // 0-1
  min?: number;
  max?: number;
  orientation: SliderOrientation;
  onChange: (value: number) => void;
  onClick?: (value: number) => void;
  size?: 'medium' | 'large';
};

export const Slider: FC<SliderProps> = ({
  className,
  trackClassName,
  fillClassName,
  scrubberClassName,
  value,
  min = 0,
  max = 1,
  orientation,
  tabIndex = 0,
  ariaLabel,
  ariaValueText,
  ariaValueMin = min,
  ariaValueMax = max,
  ariaValueNow = value,
  additionalElements,
  onChange,
  onClick,
  size = 'medium',
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const isHorizontal = orientation === 'horizontal';
  const normalizedValue = Math.min(Math.max((value - min) / (max - min), 0), 1);

  const getValueFromEvent = useCallback(
    (
      event: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent,
    ): number => {
      if (!sliderRef.current) {
        return value;
      }

      const rect = sliderRef.current.getBoundingClientRect();
      let coordinate: number;
      let dimension: number;

      if ('touches' in event) {
        const touch = event.touches[0] || event.changedTouches[0];
        coordinate = isHorizontal ? touch?.clientX || 0 : touch?.clientY || 0;
      } else {
        coordinate = isHorizontal ? event.clientX : event.clientY;
      }

      if (isHorizontal) {
        dimension = rect.width;
        coordinate = coordinate - rect.left;
      } else {
        dimension = rect.height;
        coordinate = coordinate - rect.top;
        // Invert for vertical (top = max, bottom = min)
        coordinate = dimension - coordinate;
      }

      const percentage = Math.min(Math.max(coordinate / dimension, 0), 1);
      return min + percentage * (max - min);
    },
    [value, min, max, isHorizontal],
  );

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      setIsDragging(true);
      const newValue = getValueFromEvent(event);
      onChange(newValue);
    },
    [getValueFromEvent, onChange],
  );

  const handleTouchStart = useCallback(
    (event: React.TouchEvent) => {
      event.preventDefault();
      setIsDragging(true);
      const newValue = getValueFromEvent(event);
      onChange(newValue);
    },
    [getValueFromEvent, onChange],
  );

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging) {
        return;
      }
      event.preventDefault();
      const newValue = getValueFromEvent(event);
      onChange(newValue);
    },
    [isDragging, getValueFromEvent, onChange],
  );

  const handleTouchMove = useCallback(
    (event: TouchEvent) => {
      if (!isDragging) {
        return;
      }
      event.preventDefault();
      const newValue = getValueFromEvent(event);
      onChange(newValue);
    },
    [isDragging, getValueFromEvent, onChange],
  );

  const handleEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (!onClick) {
        return;
      }

      if (
        event.target === sliderRef.current ||
        (event.target as Element).closest(`.${styles.track}`)
      ) {
        const newValue = getValueFromEvent(event);
        onClick(newValue);
      }
    },
    [onClick, getValueFromEvent],
  );

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, handleMouseMove, handleTouchMove, handleEnd]);

  const fillStyle = isHorizontal
    ? { width: `${normalizedValue * 100}%` }
    : { height: `${normalizedValue * 100}%` };

  // Position scrubber accounting for its size (12px scrubber, so 6px offset)
  // This ensures the scrubber stays fully within bounds
  const scrubberSize = size === 'large' ? 16 : 12;
  const scrubberSizeStyle = {
    width: scrubberSize,
    height: scrubberSize,
  };
  const scrubberPercentage = Math.min(Math.max(normalizedValue * 100, 0), 100);
  const scrubberStyle = isHorizontal
    ? { left: `${scrubberPercentage}%`, ...scrubberSizeStyle }
    : {
        bottom: `calc(${scrubberPercentage}% - ${scrubberSize / 2}px)`,
        ...scrubberSizeStyle,
      };

  return (
    <div
      className={classNames(
        styles.container,
        {
          [styles.horizontal]: isHorizontal,
          [styles.vertical]: !isHorizontal,
        },
        className,
      )}
      tabIndex={tabIndex}
      role="slider"
      aria-label={ariaLabel}
      aria-valuemin={ariaValueMin}
      aria-valuemax={ariaValueMax}
      aria-valuenow={ariaValueNow}
      aria-valuetext={ariaValueText}
      aria-orientation={orientation}
    >
      <div
        ref={sliderRef}
        className={classNames(styles.track, trackClassName)}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onClick={handleClick}
      >
        {additionalElements}
        <div
          className={classNames(styles.fill, fillClassName)}
          style={fillStyle}
        />
        <div
          className={classNames(
            styles.scrubber,
            { [styles.dragging]: isDragging },
            scrubberClassName,
          )}
          style={scrubberStyle}
        />
      </div>
    </div>
  );
};
